import './index.scss';

import { Portal } from 'react-portal';
import { useGAViewContent } from '../../utils/googleAnalytics';
import { useSnapChatViewContentRestaurantInfo } from '../../utils/snapchatAds';
import BottomNavBar from './BottomNavBar';
import { Gallery } from './Page/Components';
import { FilterPopupContainer, FiltersContainer } from './Filters';
import React, {
    RefObject,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import productBg from '../../assets/images/productbg.jpg';
import Section from '../../Components/Elements/Section';
import Box from 'Components/Box';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, selectDeviceType } from '../../core/redux/reducers';
import cls from 'classnames';
import ProductList from './Components/ProductList';
import ProductMap from './Map';
import ProductPreview from './Preview';
import scrollToEl from '../../plugins/scrollToEl';
import qsa from 'dom-helpers/querySelectorAll';
import ownDoc from 'dom-helpers/ownerDocument';
import ProductPageBoxTop from './Preview/ProductPageBoxTop';
import LazyLoad, { forceCheck } from 'react-lazyload';
import MobileProductCard from './Preview/MobileProductCard';
import MiniFilter from '../Header/MiniFilterButton';
import { useShowSoldoutNotification } from '../../core/hooks';
import {
    useAutoReloadProductInMobileDevice,
    useScrollToProductListView,
} from './hooks';
import addCss from 'dom-helpers/css';
import {
    actions as navActions,
    makeSelectNavIsActive,
    NavTypes,
} from '../../core/redux/features/shared/nav';
import { Route } from 'react-router';
import ProductPage from './Page';
import { DeviceType } from '../../core/redux/features/shared/types';
import { CSSTransition } from 'react-transition-group';
import { addClass, removeClass } from 'dom-helpers';
import { FilterStateProvider } from './Filters/store';
import {
    useDataLayerViewProductDetails,
    useDataLayerViewItemList,
} from 'utils/dataLayer';

function ProductMapContainer({
    viewType,
    onSelectProduct,
    deviceType,
}: {
    viewType: string;
    onSelectProduct: (e: SyntheticEvent) => void;
    deviceType: DeviceType;
}) {
    if (viewType !== 'map') {
        return null;
    }

    if (deviceType === 'mobile') {
        return (
            <Portal>
                <ProductMap onSelectProduct={onSelectProduct} />
            </Portal>
        );
    }
    return <ProductMap onSelectProduct={onSelectProduct} />;
}

export const ProductsContainer = () => {
    const dispatch = useDispatch();
    const { deviceType, activeViewType } = useSelector((state: AppState) => ({
        deviceType: selectDeviceType(state),
        activeViewType: state.viewTypes.activeViewType,
    }));
    const listElRef = useRef();

    useShowSoldoutNotification();
    useAutoReloadProductInMobileDevice();

    //
    // handlers
    //

    // Handle product preview element for map that will use to scroll to its position
    const previewRef: RefObject<HTMLDivElement> = useRef(null);
    const onSelectProduct = useScrollToProductListView();

    // auto scroll product page
    const pageRef: RefObject<HTMLDivElement> = useRef(null);
    const handleScrollToPage = useCallback(() => {
        if (pageRef.current && deviceType !== 'mobile') {
            scrollToEl(pageRef.current, document.body, -20);
        }
    }, [deviceType]);

    // remove preload content from backend
    useEffect(() => {
        const $el = qsa(ownDoc(), '#eb-preload');
        if ($el.length > 0) {
            $el[0].parentNode.removeChild($el['0']);
        }
    }, []);

    const selectNavIsActive = useMemo(makeSelectNavIsActive, []);
    const activeNavFilter = useSelector((state: AppState) =>
        selectNavIsActive(state, NavTypes.Filter),
    );
    useEffect(() => {
        if (!activeNavFilter && deviceType === 'mobile') {
            forceCheck();
            window.scroll(0, 0);
        }
    }, [activeNavFilter, deviceType]);

    useEffect(() => {
        const elFooter = qsa(document, '.footer-wrap');
        if (elFooter.length > 0) {
            addCss(elFooter[0], {
                display: '',
            });
        }
    }, []);

    useSnapChatViewContentRestaurantInfo();
    // useGAViewContent();
    useDataLayerViewProductDetails();
    useDataLayerViewItemList();

    const localeData = window.__defaultLocaleData__ || null;
    return (
        <FilterStateProvider>
            {deviceType === 'mobile' ? (
                <MiniFilter
                    isNavPin={activeViewType === 'map'}
                    isPin={activeViewType === 'map'}
                />
            ) : null}
            <Section
                data-testid="product"
                className={cls(
                    deviceType !== 'mobile' && 'product-module',
                    deviceType === 'mobile' &&
                    'section-product-list section-product-page',
                )}
                bgColor={deviceType !== 'mobile' ? '#0D1121' : null}
                bgImage={null}
            >
                <div className="section-products is-hidden-mobile">
                    <Box className="" code="top-product-page" />
                </div>

                {deviceType !== 'mobile' ? <FiltersContainer /> : null}

                <div className={cls(deviceType !== 'mobile' && 'product-list-wrap')}>
                    <div className={cls('section-product-list')}>
                        <div
                            className={cls(
                                'product-content',
                                activeViewType && 'v-' + activeViewType,
                            )}
                        >
                            <div
                                ref={listElRef}
                                className={cls(
                                    deviceType !== 'mobile' && 'grid-list',
                                    deviceType === 'mobile' && 'product-list-lz',
                                )}
                            >
                                {deviceType === 'mobile' && <ProductPageBoxTop />}
                                <ProductList onSelectProduct={onSelectProduct}>
                                    {item =>
                                        deviceType !== 'mobile' ? (
                                            item.name
                                        ) : (
                                            <LazyLoad once={true} offset={340 * 7} height={340}>
                                                <MobileProductCard product={item} />
                                            </LazyLoad>
                                        )
                                    }
                                </ProductList>
                                {deviceType === 'mobile' && localeData?.value === "da" && <BottomNavBar />}
                            </div>

                            <ProductMapContainer
                                deviceType={deviceType}
                                viewType={activeViewType}
                                onSelectProduct={onSelectProduct}
                            />
                        </div>
                    </div>
                    {deviceType !== 'mobile' ? (
                        <ProductPreview
                            onClickSeeMore={handleScrollToPage}
                            onClickMenu={handleScrollToPage}
                            previewRef={previewRef}
                        />
                    ) : null}
                </div>
            </Section>

            <Route
                path={[
                    '/restaurant/:slug',
                    '/bar/:slug',
                    '/takeaway/:slug',
                    '/en/restaurant/:slug',
                    '/en/bar/:slug',
                    '/en/takeaway/:slug',
                ]}
            >
                {({ match }) => {
                    if (deviceType !== 'mobile') {
                        if (!match) {
                            return null;
                        }
                        return (
                            <div id="product-page-wrap" ref={pageRef}>
                                <div className="container has-shadow-desktop">
                                    <ProductPage />
                                </div>
                            </div>
                        );
                    }

                    return (
                        <Portal node={document.body}>
                            <CSSTransition
                                in={match !== null}
                                timeout={250}
                                classNames="page-transition"
                                unmountOnExit
                                onExiting={() => {
                                    dispatch(navActions.hideMenu('main', false));
                                    if (listElRef.current) {
                                        removeClass(listElRef.current, 'product-list--enter');
                                        addClass(listElRef.current, 'product-list--exit');
                                    }
                                }}
                                onEntering={() => {
                                    setTimeout(() => {
                                        if (listElRef.current) {
                                            removeClass(listElRef.current, 'product-list--exit');
                                            addClass(listElRef.current, 'product-list--enter');
                                        }
                                    }, 200);
                                }}
                                onEntered={() =>
                                    setTimeout(
                                        () => dispatch(navActions.hideMenu('main', true)),
                                        500,
                                    )
                                }
                            >
                                <div className="page-transition">
                                    <ProductPage className="page-transition__content" />
                                    <div className="page-transition__shadow" />
                                </div>
                            </CSSTransition>
                        </Portal>
                    );
                }}
            </Route>

            <Gallery />
            <FilterPopupContainer />
        </FilterStateProvider>
    );
};
